/**
 * SlugValidationModal — sous-modal de validation Claude pour les slugs Tier 2.
 *
 * Flux UX :
 *   1. L'opératrice tape `label_fr` dans le formulaire principal
 *      (EmotionalIntentsTab / TargetAudiencesTab).
 *   2. Au submit, on ouvre cette sous-modal qui appelle Claude via
 *      `POST /api/catalog/slug-suggest` avec `{ kind, label_fr }`.
 *   3. Claude renvoie : suggested_slug + rationale + alternatives, OU
 *      refused=true si le concept est trop vague/intraduisible.
 *   4. L'opératrice peut accepter, éditer le slug, ou choisir une
 *      alternative — puis confirmer.
 *   5. Confirm → callback `onConfirm(slug)` du parent qui poste réellement
 *      le `POST /api/catalog/{kind}` avec le slug validé.
 *
 * Ce composant est un "wizard" : il NE fait PAS le POST de création — il
 * retourne juste un slug accepté au parent.
 */

import { useEffect, useRef, useState } from 'react'
import { Sparkles, AlertCircle } from 'lucide-react'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import Button from '@/components/ui/Button'
import Modal from '@/components/ui/Modal'
import { FormField } from '@/components/forms/FormField'
import { useSuggestSlug } from '@/hooks/useCatalog'
import {
  slugConfirmSchema,
  type SlugConfirmFormParsed,
  type SlugConfirmFormValues,
} from '@/schemas/catalog'
import type { SlugSuggestKind } from '@/types/catalog'

interface SlugValidationModalProps {
  isOpen: boolean
  kind: SlugSuggestKind
  labelFr: string
  /** Callback quand l'opératrice confirme — le parent fait le POST réel. */
  onConfirm: (slug: string) => Promise<void>
  onCancel: () => void
}

export default function SlugValidationModal({
  isOpen,
  kind,
  labelFr,
  onConfirm,
  onCancel,
}: SlugValidationModalProps) {
  const suggestMutation = useSuggestSlug()
  const [error, setError] = useState<string | null>(null)

  const form = useForm<SlugConfirmFormValues, unknown, SlugConfirmFormParsed>({
    resolver: zodResolver(slugConfirmSchema),
    defaultValues: { slug: '' },
  })

  // Garde anti-double-fire : on ne lance la suggestion qu'une fois par
  // ouverture (le parent recrée la modal avec une nouvelle prop `labelFr`
  // à chaque tour). Stocker la signature `(kind, labelFr)` permet de
  // re-fetch si l'opératrice change le label sans fermer la modal.
  const lastFiredKey = useRef<string | null>(null)

  useEffect(() => {
    if (!isOpen) {
      lastFiredKey.current = null
      return
    }
    const key = `${kind}::${labelFr}`
    if (lastFiredKey.current === key) return
    lastFiredKey.current = key

    suggestMutation.mutate(
      { kind, label_fr: labelFr },
      {
        onSuccess: (result) => {
          if (!result.refused && result.suggested_slug) {
            form.setValue('slug', result.suggested_slug, { shouldValidate: true })
          }
        },
        onError: (err) => {
          setError(err.detail ?? 'Validation Claude impossible')
        },
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, kind, labelFr])

  const handleConfirm = async (values: SlugConfirmFormParsed) => {
    setError(null)
    try {
      await onConfirm(values.slug)
    } catch (err) {
      const detail = (err as { detail?: string })?.detail ?? 'Création impossible'
      setError(detail)
    }
  }

  const result = suggestMutation.data
  const isPending = suggestMutation.isPending
  const submitting = form.formState.isSubmitting

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title="Validation du slug"
      kicker={`Claude · « ${labelFr} »`}
      size="form"
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleConfirm)} noValidate>
          <Modal.Body className="settings-form-grid">
            {isPending && (
              <p className="settings-slug-suggest-loading">
                <Sparkles size={14} aria-hidden="true" /> Claude réfléchit au meilleur slug anglais
                sémantique…
              </p>
            )}

            {!isPending && result?.refused && (
              <p className="settings-slug-suggest-refused">
                <AlertCircle size={14} aria-hidden="true" /> Concept refusé
              </p>
            )}

            {!isPending && result?.rationale && (
              <p className="settings-slug-suggest-rationale">{result.rationale}</p>
            )}

            {!isPending && !result?.refused && (
              <FormField
                name="slug"
                label="Slug retenu"
                required
                helper="Slug ANGLAIS envoyé à Claude S3 lors de la génération de drafts. Tu peux l'éditer."
                placeholder="melancholic, young_collector…"
                focusOnMount
              />
            )}

            {!isPending && result && (result.alternatives ?? []).length > 0 && (
              <div>
                <p className="ui-text-muted ui-body-sm">Alternatives proposées :</p>
                <div className="settings-slug-suggest-alternatives">
                  {(result.alternatives ?? []).map((alt) => (
                    <Button
                      key={alt}
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="settings-slug-suggest-alt-button"
                      onClick={() =>
                        form.setValue('slug', alt, { shouldValidate: true, shouldDirty: true })
                      }
                    >
                      {alt}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {error && <p className="settings-slug-suggest-refused">{error}</p>}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="ghost" type="button" onClick={onCancel} disabled={submitting}>
              Annuler
            </Button>
            <Button
              variant="primary"
              type="submit"
              loading={submitting}
              disabled={isPending || (result?.refused ?? false)}
            >
              Confirmer le slug
            </Button>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  )
}
